/**
 * @copyright 2023 Honda Motor Co., Ltd. All rights reserved.
 */

import {
  FC,
  useState,
  SyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useCallback,
} from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  Collapse,
  Typography,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import {
  NotificationBar,
  NotificationBarProps,
} from "components/Common/NotificationBar";
import { BorderedButton } from "components/Common/BorderedButton";
import LogoIcon from "assets/honda_logo.svg";
import OrderImg from "assets/ORDER.png";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProjectInfo, useUserContext } from "context/UserContext";
import { NotificationListDialog } from "components/Notification/NotificationListDialog";
import { MenuSidebar } from "components/Menu/MenuSidebar";
import { EditTrafficDialog } from "components/EditTraffic/EditTrafficDialog";
import { NotificationIconButton } from "components/Notification/NotificationIconButton";
import { Const } from "const";
import { useMapDataWorker } from "hooks/MapDataWorker";
import { useAwvDataInterval } from "hooks/AwvDataWorker";
import { useMapDataContext } from "context/MapDataProvider";
import { TaskCompleteAlert } from "components/Common/TaskCompleteAlert";
import { ErrorDialog } from "./ErrorDialog";
import { SpinnerBack } from "./SpinnerBack";
import { useAwvDataContext } from "context/AwvDataProvider";

export interface CommonHeaderProps {
  setJobsite: Dispatch<SetStateAction<ProjectInfo | undefined>>;
}

export const CommonHeader: FC<CommonHeaderProps> = ({ setJobsite }) => {
  const { t } = useTranslation();
  const user = useUserContext();
  const { mapVer, setMapVer, reset: resetMap } = useMapDataContext();
  const { reset: resetAwv } = useAwvDataContext();
  const location = useLocation();

  const [alert, setAlert] = useState<NotificationBarProps>();
  const [showNotification, setShowNotification] = useState(false);
  const [trafficEditing, setTrafficEditing] = useState(false);
  const [showCompleteMsg, setShowCompleteMsg] = useState({
    isOpen: false,
    label: "",
  });

  // #region Menu
  const [openMenu, setMenuOpen] = useState(false);
  const handleMenuClose = () => {
    setMenuOpen(false);
  };
  // #endregion

  /**
   * Action for jobsite changed from menu
   * @param {ProjectInfo} jobsite Jobsite
   */
  const handleJobsiteChange = useCallback(
    (jobsite?: ProjectInfo) => {
      setJobsite((current) => {
        if (
          current?.jobSiteId !== jobsite?.jobSiteId ||
          current?.geoSiteId !== jobsite?.geoSiteId ||
          current?.mapId !== jobsite?.mapId
        ) {
          // Reset Map and AWV
          resetMap();
          resetAwv();
        }
        return jobsite;
      });
    },
    [resetAwv, resetMap, setJobsite]
  );
  const completeAlertClose = useCallback(() => {
    setShowCompleteMsg((current) => {
      return { ...current, isOpen: false };
    });
  }, []);

  useEffect(() => {
    if (mapVer.mapVer > 0 && mapVer.necessityOfUpdate) {
      if (trafficEditing) {
        setMapVer((current) => {
          current.necessityOfUpdate = false;
          return current;
        });
      } else {
        setShowCompleteMsg({ isOpen: true, label: t("AWVUI-MSG61") });
      }
    }
  }, [mapVer.mapVer, mapVer.necessityOfUpdate, setMapVer, t, trafficEditing]);

  const isMain = [
    Const.path.root,
    Const.path.map,
    Const.path.tasklist,
    Const.path.multiple,
  ].includes(location.pathname);

  return (
    <>
      <HeaderAppBar position="relative">
        <HeaderToolbar disableGutters>
          <Logo>
            <img src={LogoIcon} alt="HONDA Logo" />
          </Logo>
          <Project sx={{ maxWidth: `calc(50% - 160px - 152px)` }}>
            <img src={OrderImg} alt="ORDER" />
            <label>
              <Typography
                variant="inherit"
                sx={{
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {user.jobsite?.jobSiteName}
              </Typography>
            </label>
          </Project>
          <DemoIcon />
          <Spacer />
          {isMain && (
            <MainHeaderParts
              setAlert={setAlert}
              setShowNotification={setShowNotification}
              setTrafficEditing={setTrafficEditing}
              setMenuOpen={setMenuOpen}
            />
          )}
        </HeaderToolbar>
      </HeaderAppBar>
      {isMain && (
        <>
          <NotificationTransition in={!!alert}>
            <NotificationBar
              onClose={() => {
                setAlert(undefined);
              }}
              {...alert}
            />
          </NotificationTransition>
          <MenuSidebar
            openMenu={openMenu}
            onMenuClose={handleMenuClose}
            onJobsiteChanged={handleJobsiteChange}
          />
          {showNotification && (
            <NotificationListDialog
              onClose={() => {
                setShowNotification(false);
              }}
            />
          )}
          {trafficEditing && (
            <EditTrafficDialog
              open={trafficEditing}
              onClose={(isSave) => {
                if (isSave) {
                  setShowCompleteMsg({ isOpen: true, label: t("AWVUI-MSG05") });
                }
                setTrafficEditing(false);
              }}
            />
          )}
          <TaskCompleteAlert
            {...showCompleteMsg}
            onClose={completeAlertClose}
          />
        </>
      )}
    </>
  );
};

interface Props {
  setAlert: Dispatch<SetStateAction<NotificationBarProps | undefined>>;
  setTrafficEditing: Dispatch<SetStateAction<boolean>>;
  setShowNotification: Dispatch<SetStateAction<boolean>>;
  setMenuOpen: Dispatch<SetStateAction<boolean>>;
}

const MainHeaderParts: FC<Props> = ({
  setAlert,
  setTrafficEditing,
  setShowNotification,
  setMenuOpen,
}) => {
  const { t } = useTranslation();
  const { roleName } = useUserContext();

  // Periodically retrieve the MAP data and Awv data from the server.
  const { loading, value, error } = useMapDataWorker();
  useAwvDataInterval();
  const { mapData } = useMapDataContext();

  const [first, setFirst] = useState(true);
  const [showError, setShowError] = useState(false);

  // #region Tabs
  const location = useLocation();
  const [tabPage, setTabPage] = useState(
    location.pathname === Const.path.root ? Const.path.map : location.pathname
  );
  const navigate = useNavigate();
  const handleChangeTab = (e: SyntheticEvent<Element, Event>, v: string) => {
    setTabPage(v);
    navigate(v, { replace: true });
  };
  // #endregion

  // Effect for get map API error occurred
  useEffect(() => {
    if (!loading) {
      if (error) {
        setFirst(false);
        setShowError((pre) => pre || first);
      }
      if (value?.mapInfo.mapId) {
        setFirst(false);
      }
    }
  }, [loading, error, value, first]);

  const handleClickRoadClosure = useCallback(() => {
    setTrafficEditing(true);
  }, [setTrafficEditing]);
  const handleClickNotifications = () => {
    setShowNotification(true);
  };
  const handleClickProfile = () => {
    setMenuOpen(true);
  };

  return (
    <>
      <TabContainer>
        <Tabs value={tabPage} onChange={handleChangeTab} centered>
          <Tab value={Const.path.map} label={t("AWVUI-MSG70")} />
          <Tab
            value={Const.path.multiple}
            label={t("AWVUI-MSG187")}
          />
          <Tab value={Const.path.tasklist} label={t("AWVUI-MSG71")} />
        </Tabs>
      </TabContainer>
      <ButtonPanel>
        <BorderedButton
          onClick={handleClickRoadClosure}
          disabled={roleName === "awv_operator"}
        >
          {t("AWVUI-MSG72")}
        </BorderedButton>
      </ButtonPanel>
      <IconButtonPanel>
        <NotificationIconButton
          setAlert={setAlert}
          onClick={handleClickNotifications}
        />
        <IconButton size="large" onClick={handleClickProfile} color="secondary">
          <AccountCircleIcon fontSize="inherit" />
        </IconButton>
      </IconButtonPanel>
      <ErrorDialog
        open={showError}
        message={t("AWVUI-MSG165")}
        positiveButtonText={t("AWVUI-MSG22")}
        onPositiveButtonClick={() => {
          setShowError(false);
        }}
      />
      {mapData.mapId === "" && !showError && <SpinnerBack />}
    </>
  );
};

const HeaderAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.appBar,
}));

const HeaderToolbar = styled(Toolbar)({
  minHeight: "auto !important",
});

const Logo = styled(Box)(({ theme }) => ({
  margin: theme.spacing(0, 2),
  img: {
    width: 104,
  },
}));

const Project = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  paddingTop: `calc(${theme.typography.caption.fontSize} * 1.4)`,
  img: {
    width: 56,
  },
  label: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.4,
  },
}));

const Spacer = styled(Box)({
  flexGrow: 1,
});

const ButtonPanel = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(2),
}));

const IconButtonPanel = styled(Box)(({ theme }) => ({
  marginRight: theme.spacing(1),
}));

const TabContainer = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: 0,
  right: 0,
  bottom: 0,
  ".MuiTab-root": {
    minWidth: "160px",
    color: theme.palette.text.primary,
    fontSize: theme.typography.body1.fontSize,
    fontWeight: "bold",
    "&.Mui-selected": {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
    },
  },
  ".MuiTabs-indicator": {
    boxSizing: "content-box",
    backgroundColor: "transparent",
    height: theme.spacing(0.25),
    borderTopWidth: theme.spacing(0.5),
    borderTopStyle: "solid",
    borderTopColor: theme.palette.primary.main,
  },
}));

const NotificationTransition = styled(Collapse)(({ theme }) => ({
  flexShrink: 0,
  zIndex: theme.zIndex.appBar,
}));

const DemoIcon = () => {
  return (
    <svg
      width="100"
      height="45"
      viewBox="0 0 120 40"
      xmlns="http://www.w3.org/2000/svg"
    >
      <rect
        x="10"
        y="5"
        width="100"
        height="30"
        rx="15"
        ry="15"
        fill="none"
        stroke="red"
        strokeWidth="1"
      />
      <text
        x="60"
        y="25"
        fontSize="16"
        fontWeight="bold"
        textAnchor="middle"
        fill="red"
      >
        デモ版
      </text>
    </svg>
  );
};

export default DemoIcon;